<script>
import WhatsappMessagePreview from 'dashboard/routes/dashboard/settings/campaigns/WhatsappMessagePreview.vue';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    WhatsappMessagePreview,
  },
  props: {
    template: {
      type: Object,
      default: null,
    },
  },
  emits: ['edit', 'delete'],
  computed: {
    isPending() {
      return this.template?.status?.trim().toUpperCase() === 'PENDING';
    },
  },
  methods: {
    getStatusBadgeClass(status) {
      const statusClasses = {
        APPROVED: 'bg-n-teal-3 text-n-teal-11',
        PENDING: 'bg-n-amber-3 text-n-amber-11',
        REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
      };
      return statusClasses[status] || 'bg-n-slate-3 text-n-slate-11';
    },
    getTemplateBody(template) {
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      return bodyComponent?.text || '';
    },
    getStatusLabel(status) {
      const key = `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.STATUS.${status?.toUpperCase()}`;
      const translated = this.$t(key);
      return translated !== key ? translated : status;
    },
    getCategoryLabel(category) {
      const key = `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CATEGORIES.${category?.toUpperCase()}`;
      const translated = this.$t(key);
      return translated !== key ? translated : category;
    },
    handleEditClick() {
      if (this.isPending) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.PENDING_ALERT')
        );
        return;
      }
      this.$emit('edit', this.template);
    },
  },
};
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-n-solid-1">
    <!-- Empty State -->
    <div v-if="!template" class="flex-1 flex items-center justify-center">
      <div class="text-center text-n-slate-11">
        <fluent-icon
          icon="document"
          size="48"
          class="mx-auto mb-3 opacity-50"
        />
        <p class="text-sm">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.SELECT_PLACEHOLDER') }}
        </p>
      </div>
    </div>

    <!-- Template Details -->
    <div v-else class="flex-1 flex flex-col overflow-y-auto p-6">
      <!-- Template Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4"
      >
        <div>
          <h3 class="text-xl font-semibold text-n-slate-12 break-all">
            {{ template.name }}
          </h3>
          <div class="flex flex-wrap items-center gap-3 mt-2">
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full"
              :class="getStatusBadgeClass(template.status)"
            >
              {{ getStatusLabel(template.status) }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ template.language }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ getCategoryLabel(template.category) }}
            </span>
          </div>
        </div>
        <div class="flex gap-2 shrink-0">
          <div
            v-if="isPending"
            v-tooltip.top="
              $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.PENDING_TOOLTIP')
            "
          >
            <woot-button variant="smooth" size="small" icon="edit" disabled>
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.BUTTON') }}
            </woot-button>
          </div>
          <woot-button
            v-else
            variant="smooth"
            size="small"
            icon="edit"
            @click="handleEditClick"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.BUTTON') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="alert"
            size="small"
            icon="delete"
            @click="$emit('delete', template)"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.DELETE.BUTTON') }}
          </woot-button>
        </div>
      </div>

      <!-- Template Body -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.BODY_SECTION') }}
        </h4>
        <p
          class="text-sm text-n-slate-12 bg-n-slate-2 p-3 rounded-lg font-mono whitespace-pre-wrap border border-n-weak"
        >
          {{ getTemplateBody(template) }}
        </p>
      </div>

      <!-- Preview -->
      <div>
        <h4 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.PREVIEW_SECTION') }}
        </h4>
        <div class="max-w-md">
          <WhatsappMessagePreview :template="template" />
        </div>
      </div>
    </div>
  </div>
</template>
